import { Button } from "@fieldwork/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@fieldwork/ui/components/dropdown-menu";
import { Skeleton } from "@fieldwork/ui/components/skeleton";
import { Link, useNavigate } from "@tanstack/react-router";

import { authClient, forgetSession } from "@/lib/auth-client";

export function initials(name: string | undefined) {
  return (name ?? "?")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Avatar and name at the right of the header; opens a menu with the email and Sign out. */
export default function UserMenu() {
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return <Skeleton className="h-8 w-32 rounded-md" />;
  }

  if (!session) {
    return (
      <Link to="/login">
        <Button variant="outline" size="sm">
          Sign in
        </Button>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Account: ${session.user.name}`}
        className="flex cursor-pointer items-center gap-2.5 rounded-md border-0 bg-transparent p-0 text-[#ECEAE4] outline-none focus-visible:ring-1 focus-visible:ring-[#FF4F1A]"
      >
        <span
          aria-hidden="true"
          className="grid size-8 place-items-center rounded-full bg-[#2A2927] font-mono text-xs font-bold"
        >
          {initials(session.user.name)}
        </span>
        <span className="text-[13px]">{session.user.name}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="text-[13px] text-[#ECEAE4]">{session.user.name}</span>
            <span className="truncate text-xs text-[#A8A59D]">{session.user.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              forgetSession();
              authClient.signOut({
                fetchOptions: {
                  onSuccess: () => {
                    navigate({
                      to: "/",
                    });
                  },
                },
              });
            }}
          >
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
